import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import apiClient from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import { FaEdit, FaTimes } from 'react-icons/fa';
import SearchAllocation from '../allocation/SearchAllocation';
import './ComplaintManagement.css';

const AddComplaint = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [formData, setFormData] = useState({
    allocation_id: '',
    complaint_type: '',
    description: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState(null);
  const [showAllocationSelector, setShowAllocationSelector] = useState(false);
  const [complaintTypes, setComplaintTypes] = useState([]);

  // Load complaint types on component mount
  useEffect(() => {
    fetchComplaintTypes();
  }, []);

  const fetchComplaintTypes = async () => {
    try {
      const response = await apiClient.get('/complaints/types');
      const types = response.data || [];
      // Extract type names for dropdown
      setComplaintTypes(types.map(type => type.type_name || type));
    } catch (error) {
      console.error('Error fetching complaint types:', error);
      // Fallback to default types if API fails
      setComplaintTypes([
        'Maintenance Issue',
        'Cleanliness',
        'Noise Complaint',
        'Facility Issue',
        'Staff Behavior',
        'Safety Concern',
        'Food Quality',
        'Room Issue',
        'Internet/WiFi',
        'Electricity',
        'Water Supply',
        'Other'
      ]);
    }
  };

  const handleAllocationSelect = (allocation) => {
    setSelectedAllocation(allocation);
    setFormData(prev => ({
      ...prev,
      allocation_id: allocation.allocation_id
    }));
    setShowAllocationSelector(false);
    
    // Clear allocation error
    if (errors.allocation_id) {
      setErrors(prev => ({
        ...prev,
        allocation_id: undefined
      }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const clearSelection = () => {
    setSelectedAllocation(null);
    setFormData(prev => ({
      ...prev,
      allocation_id: ''
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.allocation_id) {
      newErrors.allocation_id = 'Please select a bed allocation';
    }
    
    if (!formData.complaint_type) {
      newErrors.complaint_type = 'Please select a complaint type';
    }
    
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setLoading(true);
    try {
      const complaintData = {
        allocation_id: parseInt(formData.allocation_id),
        complaint_type: formData.complaint_type,
        description: formData.description.trim()
      };
      
      const response = await apiClient.post('/complaints', complaintData);
      
      if (response.status === 201 || response.status === 200) {
        toastSuccess('Complaint submitted successfully!');
        navigate('/complaints');
      } else {
        toastError('Failed to submit complaint');
      }
    } catch (error) {
      console.error('Error submitting complaint:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to submit complaint';
      toastError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      allocation_id: '',
      complaint_type: '',
      description: ''
    });
    setSelectedAllocation(null);
    setErrors({});
  };

  if (showAllocationSelector) {
    return (
      <div className="rt-layout">
        <Menu className="rt-menu" activePath={location.pathname} />
        <div className="rt-main-content">
          <div className="rt-title-row">
            <h2 className="rt-title">Select Bed Allocation</h2>
            <div className="rt-actions">
              <RTButton 
                variant="secondary" 
                onClick={() => setShowAllocationSelector(false)}
              >
                ← Back
              </RTButton>
            </div>
          </div>
          
          <div className="rt-main-section">
            <SearchAllocation 
              mode="Select"
              onSelect={handleAllocationSelect}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rt-layout">
      <Menu className="rt-menu" activePath={location.pathname} />
      <div className="rt-main-content">
        <div className="rt-title-row">
          <h2 className="rt-title">Add Complaint</h2>
          <div className="rt-actions">
            <RTButton variant="secondary" onClick={() => navigate(-1)}>
              ← Back
            </RTButton>
          </div>
        </div>
        
        <div className="rt-main-section">
          <form onSubmit={handleSubmit} className="complaint-form">
            <div className="complaint-form-sections">
              <div className="complaint-section">
                <h3 className="complaint-section-title">Allocation Details</h3>
                
                {/* Allocation Selection */}
                <div className="complaint-form-group">
                  <label>
                    Bed Allocation <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div
                    className="selection-container"
                    onClick={() => setShowAllocationSelector(true)}
                  >
                    {selectedAllocation ? (
                      <div className="selection-filled">
                        <div className="selected-info">
                          <div className="selected-name">
                            {selectedAllocation.pg_name} - {selectedAllocation.building_name}
                          </div>
                          <div className="selected-details">
                            Room: {selectedAllocation.room_type} | Mobile: {selectedAllocation.pg_mobile}
                          </div>
                        </div>
                        <div className="selection-actions">
                          <button
                            type="button"
                            className="icon-button edit-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowAllocationSelector(true);
                            }}
                            title="Change Selection"
                          >
                            <FaEdit />
                          </button>
                          <button
                            type="button"
                            className="icon-button clear-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearSelection();
                            }}
                            title="Clear Selection"
                          >
                            <FaTimes />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="selection-empty">
                        Click to select a bed allocation
                      </div>
                    )}
                  </div>
                  {errors.allocation_id && (
                    <span className="form-error">{errors.allocation_id}</span>
                  )}
                </div>
              </div>

              <div className="complaint-section">
                <h3 className="complaint-section-title">Complaint Information</h3>
                
                {/* Complaint Type */}
                <div className="complaint-form-group">
                  <label>
                    Complaint Type <span style={{ color: 'red' }}>*</span>
                  </label>
                  <select
                    name="complaint_type"
                    value={formData.complaint_type}
                    onChange={handleInputChange}
                  >
                    <option value="">Select complaint type</option>
                    {complaintTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.complaint_type && (
                    <span className="form-error">{errors.complaint_type}</span>
                  )}
                </div>

                {/* Description */}
                <div className="complaint-form-group">
                  <label>
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Please provide detailed description of the complaint (optional)..."
                    rows="6"
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    {formData.description.length}/500 characters
                  </div>
                  {errors.description && (
                    <span className="form-error">{errors.description}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="complaint-actions">
              <RTButton 
                type="button" 
                variant="secondary" 
                onClick={resetForm}
                disabled={loading}
              >
                Reset
              </RTButton>
              <RTButton 
                type="submit" 
                variant="primary" 
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Complaint'}
              </RTButton>
            </div>
          </form>
        </div>
        
        {/* Loading Overlay */}
        {loading && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999
          }}>
            <RTSpinner size="medium" showMessage={true} message="Processing your complaint..." />
          </div>
        )}
      </div>
    </div>
  );
};

export default AddComplaint;
